import { KatAlert } from "@amzn/katal-react";
import classnames from "classnames";
import Markdown from "markdown-to-jsx";
import React, { type FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { InboundDocumentContext } from "src/components/Contexts/InboundDocumentContext";
import DocumentComplianceHelperButton from "src/components/Pages/DocumentInbound/DocumentComplianceHelper/DocumentComplianceHelperButton";
import DocumentDetailHeader from "src/components/Pages/DocumentInbound/Header/DocumentDetailsHeader";
import InboundDocumentBreadcrumb from "src/components/Pages/DocumentInbound/InboundDocumentBreadcrumb";
import TranslationKey, {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import ComplianceUserActions from "src/components/UserActions/ComplianceUserActions";
import styles from "../../../styles.module.scss";

const shouldShowHelperButton = (status: string): boolean => {
  return (
    status === "temporarily rejected" ||
    status === "pending actions" ||
    status === "rejected"
  );
};

const InboundDocumentHeader: FC = () => {
  const inboundDocument = useContext(InboundDocumentContext);
  const { t, i18n } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );

  const COMMON_CLASSNAMES = [
    styles.flex,
    styles.flex_direction_row,
    styles.flex_nowrap,
  ];

  const keyBuilder = new TranslationKey(
    inboundDocument.countryCode,
    inboundDocument.documentType,
    inboundDocument.documentSubtype,
    inboundDocument.useCase
  );

  let bannerHeaderKey: string | undefined;
  let bannerBodyKey: string | undefined;
  let bannerVariant: KatAlert.Variant | undefined;

  const keyPrefix = keyBuilder.buildInboundRouteKey("document_banner", "aic");

  // Order matters here. Danger should take place over warning and warning over info
  const bannerTypes: KatAlert.Variant[] = [
    "success",
    "info",
    "warning",
    "danger",
  ];

  bannerTypes.forEach((bannerType) => {
    const headerKey = `${keyPrefix}_${bannerType}_header`;
    const bodyKey = `${keyPrefix}_${bannerType}_body`;

    if (i18n.exists(headerKey) && i18n.exists(bodyKey)) {
      bannerHeaderKey = headerKey;
      bannerBodyKey = bodyKey;
      bannerVariant = bannerType;
    }
  });

  return (
    <div className={classnames(styles.mt_6)}>
      <div className={classnames(...COMMON_CLASSNAMES, styles.w_100)}>
        <div
          className={classnames(
            ...COMMON_CLASSNAMES,
            styles.w_70,
            styles.justify_start
          )}
        >
          <InboundDocumentBreadcrumb />
        </div>
      </div>
      {bannerBodyKey && bannerHeaderKey && bannerVariant ? (
        <div>
          <KatAlert
            persistent={false}
            header={t(bannerHeaderKey)}
            variant={bannerVariant}
          >
            <Markdown>{t(bannerBodyKey)}</Markdown>
          </KatAlert>
        </div>
      ) : (
        <></>
      )}
      <div className={classnames(...COMMON_CLASSNAMES, styles.w_100)}>
        <div
          className={classnames(
            ...COMMON_CLASSNAMES,
            styles.w_80,
            styles.justify_start
          )}
        >
          <DocumentDetailHeader
            documentDetails={inboundDocument.documentDetails}
            metadataOverrides={inboundDocument.metadataOverrides}
            content={inboundDocument}
          />
        </div>
        <div
          className={classnames(
            ...COMMON_CLASSNAMES,
            styles.w_20,
            styles.justify_end
          )}
        >
          <div
            className={classnames(
              styles.flex,
              styles.flex_direction_column,
              styles.justify_space_between
            )}
          >
            <div
              className={classnames(
                styles.flex,
                styles.align_items_start,
                styles.justify_end
              )}
            ></div>
            <div
              className={classnames(
                styles.flex,
                styles.flex_direction_column,
                styles.align_items_end,
                styles.gap_8
              )}
            >
              {inboundDocument.status &&
                shouldShowHelperButton(inboundDocument.status) && (
                  <DocumentComplianceHelperButton />
                )}
              <ComplianceUserActions />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InboundDocumentHeader;
